const DOCKING_BASE = "https://34.160.16.181.nip.io/docking-api-prod-apigee/api/docking";

function authHeaders(extra = {}) {
  const token = sessionStorage.getItem("id_token");
  return token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra };
}

function getUserProfile() {
  try {
    return JSON.parse(sessionStorage.getItem("userProfile") || "{}");
  } catch (_) {
    return {};
  }
}

async function handleResponse(res) {
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const e = await res.json();
      msg = e.detail || e.message || JSON.stringify(e);
    } catch (_) {
      try { msg = await res.text(); } catch (_) {
        // ignore parsing errors, keep original status message
      }
    }
    throw new Error(msg);
  }
  return res;
}

function parseMaybeJson(text) {
  if (!text) return null;
  try { return JSON.parse(text); } catch (_) { return text; }
}

function parseSse(text) {
  const dataLines = text
    .split(/\r?\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim())
    .filter((line) => line && line !== "[DONE]");
  if (!dataLines.length) return parseMaybeJson(text);
  return parseMaybeJson(dataLines[dataLines.length - 1]);
}

async function readJsonOrSse(res) {
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("text/event-stream")) return parseSse(await res.text());
  if (contentType.includes("application/json")) return res.json();
  return parseMaybeJson(await res.text());
}

async function requestJson(url, init = {}) {
  const res = await fetch(url, init);
  await handleResponse(res);
  return readJsonOrSse(res);
}

function addQuery(url, params = {}) {
  const u = new URL(url);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      u.searchParams.set(key, value);
    }
  });
  return u.toString();
}

function normalizeMode(gridSource) {
  return gridSource === "reference_ligand" ? "reference" : gridSource;
}

export function getResponseJobId(data) {
  return data?.job_id || data?.jobId || data?.id || data?.job || data?.run_id || data?.runId;
}

export async function fetchPdbStructure(pdbId) {
  const id = pdbId.trim().toUpperCase();
  return requestJson(`${DOCKING_BASE}/pdb-structure/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
  });
}

export async function fetchTextFromUrl(url) {
  const res = await fetch(url);
  await handleResponse(res);
  return res.text();
}

export async function submitPrepareJob({
  pdbFile,
  ligandFile,
  ph = 7.4,
  keepWater = false,
  nConformers = 1,
  callbackEnv = "dev",
  projectId,
  projectName,
  companyId,
  userId,
  userName,
} = {}) {
  const profile = getUserProfile();
  const fd = new FormData();
  if (pdbFile) fd.append("pdb_file", pdbFile);
  if (ligandFile) fd.append("ligand_file", ligandFile);

  const url = addQuery(`${DOCKING_BASE}/prepare`, {
    ph,
    keep_water: keepWater ? "true" : "false",
    n_conformers: nConformers,
    callback_env: callbackEnv,
    projectId,
    projectName,
    companyId: companyId ?? profile.companyId,
    userId:    userId    ?? profile.userId,
    userName:  userName  ?? profile.email,
  });

  return requestJson(url, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
}

export function getPrepareDownloadUrl(jobId) {
  return `${DOCKING_BASE}/prepare/download/${encodeURIComponent(jobId)}`;
}

export async function submitProteinPrep(_pdbId, pdbFile, opts = {}) {
  return submitPrepareJob({ pdbFile, ligandFile: opts.ligandFile, ...opts });
}

export async function getProteinStatus() { return { status: "completed" }; }
export async function getProteinResult() { return {}; }

export async function downloadPreparedProtein(jobId) {
  const res = await fetch(`${getPrepareDownloadUrl(jobId)}?file_type=protein_pdb`, {
    headers: authHeaders(),
  });
  await handleResponse(res);
  return res.text();
}

export function getProteinDownloadUrl(jobId) {
  return getPrepareDownloadUrl(jobId);
}

export async function downloadPrepareZipAndExtract(jobId) {
  const JSZipImport = await import("jszip");
  const JSZip = JSZipImport.default || JSZipImport;
  const url = `${DOCKING_BASE}/prepare/download/${encodeURIComponent(jobId)}`;
  const res = await fetch(url, { headers: authHeaders() });
  await handleResponse(res);
  const blob = await res.blob();
  if (blob.size < 100) throw new Error(`Response too small to be a ZIP (${blob.size} bytes)`);

  const zip = await JSZip.loadAsync(blob);
  const fileNames = Object.keys(zip.files).filter(n => !zip.files[n].dir);
  let pdbText = null, sdfText = null, jsonData = null;

  for (const filename of fileNames) {
    const file = zip.files[filename];
    const lower = filename.toLowerCase();
    if (lower.endsWith(".pdb") || lower.endsWith(".pdbqt")) {
      pdbText = await file.async("string");
    } else if (lower.endsWith(".sdf") || lower.endsWith(".mol")) {
      sdfText = await file.async("string");
    } else if (lower.endsWith(".json")) {
      const raw = await file.async("string");
      try { jsonData = JSON.parse(raw); } catch (_) { jsonData = raw; }
    }
  }
  return { pdbText, sdfText, jsonData, blob };
}

export async function submitLigandPrep(jobId, ligandFile, nConformers = 1) {
  return { job_id: jobId, ligand_file: ligandFile?.name, n_conformers: nConformers };
}

export async function getLigandPrepStatus() { return { status: "completed" }; }
export async function getLigandPrepResult() { return {}; }

export async function downloadPreparedLigand(jobId) {
  const res = await fetch(`${getPrepareDownloadUrl(jobId)}?file_type=ligand_sdf`, {
    headers: authHeaders(),
  });
  await handleResponse(res);
  return res.text();
}

export function getLigandPrepDownloadUrl(jobId) {
  return getPrepareDownloadUrl(jobId);
}

export async function submitPocketDetect(jobId, padding = 4) {
  const { companyId, userId, email } = getUserProfile();
  return requestJson(
    addQuery(`${DOCKING_BASE}/detect-pockets/${encodeURIComponent(jobId)}`, {
      padding,
      companyId,
      userId,
      userName: email,
    }),
    { method: "POST", headers: authHeaders() }
  );
}

export async function getPocketDetectResult(jobId) {
  return requestJson(`${DOCKING_BASE}/detect-pockets/result/${encodeURIComponent(jobId)}`, {
    headers: authHeaders(),
  });
}

export async function getPocketStatus() { return { status: "completed" }; }
export async function getPocketResult() { return { pockets: [] }; }

export function getPocketDownloadUrl(jobId, rank) {
  return addQuery(`${DOCKING_BASE}/detect-pockets/download/${encodeURIComponent(jobId)}`, { rank });
}

export async function submitDockingRun(jobId, mode, params = {}, opts = {}) {
  const isPocketMode = mode === "pocket";
  const profile = getUserProfile();

  const queryParams = {
    mode,
    exhaustiveness: opts.exhaustiveness ?? 8,
    n_poses: opts.nPoses ?? 5,
    n_conformers: opts.nConformers ?? 1,
    gnina_cnn_scoring: opts.gninaCnnScoring ?? "rescore",
    projectId:   opts.projectId,
    projectName: opts.projectName,
    companyId:   opts.companyId  ?? profile.companyId,
    userId:      opts.userId     ?? profile.userId,
    userName:    opts.userName   ?? profile.email,
  };

  if (isPocketMode) {
    queryParams.pocket_ranks = Array.isArray(params.pocketRanks)
      ? params.pocketRanks.join(",")
      : params.pocketRanks;
    queryParams.blind_padding = params.padding ?? opts.blindPadding ?? 4;
  } else {
    if (params.centerX !== undefined) queryParams.center_x = params.centerX;
    if (params.centerY !== undefined) queryParams.center_y = params.centerY;
    if (params.centerZ !== undefined) queryParams.center_z = params.centerZ;
    if (params.sizeX   !== undefined) queryParams.size_x   = params.sizeX;
    if (params.sizeY   !== undefined) queryParams.size_y   = params.sizeY;
    if (params.sizeZ   !== undefined) queryParams.size_z   = params.sizeZ;
    queryParams.blind_padding = params.padding ?? opts.blindPadding ?? 4;
  }

  const url = addQuery(`${DOCKING_BASE}/run/${encodeURIComponent(jobId)}`, queryParams);
  const init = { method: "POST", headers: authHeaders() };

  if (opts.modifiedProteinPdb) {
    const fd = new FormData();
    fd.append("modified_protein_pdb", opts.modifiedProteinPdb, "modified_protein.pdb");
    init.body = fd;
  }

  return requestJson(url, init);
}

export async function submitPocketDocking(jobId, pocketRanks, opts = {}) {
  return submitDockingRun(jobId, "pocket", { pocketRanks, padding: opts.padding }, opts);
}

export async function submitDirectDocking(jobId, gridSource, gridParams, opts = {}) {
  return submitDockingRun(jobId, normalizeMode(gridSource), gridParams, opts);
}

export async function getDockStatus() { return { status: "completed" }; }
export async function getPocketDockStatus() { return { status: "completed" }; }
export async function getDockResult() { return {}; }

export async function getDockComplexPdb(jobId) {
  const res = await fetch(`${DOCKING_BASE}/download/${encodeURIComponent(jobId)}`, {
    headers: authHeaders(),
  });
  await handleResponse(res);
  return res.text();
}

export async function pollDockingResultZip(jobId) {
  const JSZipImport = await import("jszip");
  const JSZip = JSZipImport.default || JSZipImport;
  const url = `${DOCKING_BASE}/download/${encodeURIComponent(jobId)}`;
  const MAX_ATTEMPTS = 20;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) { await new Promise(r => setTimeout(r, Math.min(4000 + attempt * 500, 10000))); continue; }
      const blob = await res.blob();
      if (blob.size < 100) { await new Promise(r => setTimeout(r, Math.min(4000 + attempt * 500, 10000))); continue; }

      const zip = await JSZip.loadAsync(blob);
      const fileNames = Object.keys(zip.files).filter(n => !zip.files[n].dir);
      let pdbText = null, sdfText = null, jsonData = null;

      for (const filename of fileNames) {
        const file = zip.files[filename];
        const lower = filename.toLowerCase();
        if (lower.endsWith(".pdb") || lower.endsWith(".pdbqt")) {
          pdbText = await file.async("string");
        } else if (lower.endsWith(".sdf") || lower.endsWith(".mol")) {
          sdfText = await file.async("string");
        } else if (lower.endsWith(".json")) {
          const raw = await file.async("string");
          try { jsonData = JSON.parse(raw); } catch (_) { jsonData = raw; }
        }
      }

      if (!pdbText && !sdfText) { await new Promise(r => setTimeout(r, Math.min(4000 + attempt * 500, 10000))); continue; }
      return { pdbText, sdfText, jsonData, blob };
    } catch (e) {
      await new Promise(r => setTimeout(r, Math.min(4000 + attempt * 500, 10000)));
    }
  }
  throw new Error(`[DOCK-ZIP] Gave up after ${MAX_ATTEMPTS} attempts`);
}

export async function getPocketRunHistory(jobId) {
  const res = await fetch(`${DOCKING_BASE}/run/history/${encodeURIComponent(jobId)}`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return { runs: [] };
  await handleResponse(res);
  return res.json();
}

export function getDockDownloadUrl(jobId, fileKey) {
  const map = {
    complex_sdf: `${DOCKING_BASE}/${encodeURIComponent(jobId)}?file_type=complex_sdf`,
    complex_pdb: `${DOCKING_BASE}/run/download/${encodeURIComponent(jobId)}?file_type=complex_pdb`,
    report:      `${DOCKING_BASE}/run/download/${encodeURIComponent(jobId)}?file_type=report`,
  };
  return map[fileKey] || null;
}

export function getPocketDockDownloadUrl(jobId, rank, fileKey) {
  if (fileKey === "complex_sdf") return addQuery(getDockDownloadUrl(jobId, "complex_sdf"), { rank });
  if (fileKey === "complex_pdb") return addQuery(getDockDownloadUrl(jobId, "complex_pdb"), { rank });
  return null;
}

export async function downloadFileFromUrl(url) {
  const res = await fetch(url, { headers: authHeaders() });
  await handleResponse(res);
  return {
    blob: await res.blob(),
    contentDisposition: res.headers.get("content-disposition") || "",
    contentType: res.headers.get("content-type") || "",
  };
}

export async function getInteractionDiagram(jobId, type = "png", rank = 0) {
  let url = `${DOCKING_BASE}/interaction/diagram/${encodeURIComponent(jobId)}?type=${type}`;
  if (rank > 0) url += `&rank=${rank}`;
  const res = await fetch(url, { headers: authHeaders() });
  await handleResponse(res);
  return type === "svg" ? res.text() : res.blob();
}

export async function getInteractionData(jobId, rank = 0) {
  const res = await fetch(`${DOCKING_BASE}/interaction/data/${encodeURIComponent(jobId)}?rank=${rank}`, {
    headers: authHeaders(),
  });
  await handleResponse(res);
  return res.json();
}

export async function submitDockingJob(proteinFile, ligandFile, params) {
  const { center_x, center_y, center_z, size_x, size_y, size_z, exhaustiveness, num_modes } = params;
  const prep = await submitPrepareJob({ pdbFile: proteinFile, ligandFile });
  const jobId = getResponseJobId(prep);
  if (!jobId) throw new Error("No job_id returned.");
  return submitDockingRun(jobId, "manual", {
    centerX: center_x, centerY: center_y, centerZ: center_z,
    sizeX: size_x, sizeY: size_y, sizeZ: size_z,
  }, { exhaustiveness, nPoses: num_modes });
}

export async function getDockingResult(jobId) { return getDockComplexPdb(jobId); }
export async function getInteractionResults(jobId) { return getInteractionData(jobId); }